import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  DocumentNode,
  NetworkStatus,
  QueryHookOptions,
  useApolloClient,
  useQuery,
} from '@apollo/client';
import { Kind, visit } from 'graphql';

import { getError, getTypeOrNull } from './apollo';

type QueryVariables = {
  filter?: string | null;
  pageSize?: number | null;
  pageToken?: string | null;
  orderBy?: string | null;
};

type InitialData<TItem> = {
  items: TItem[] | null | undefined;
  nextPageToken: string | null | undefined;
};

export type NewStandardListHookOptions<TCustomVars = {}, TItem = any> = {
  filter?: string;
  pageSize?: number;
  orderBy?: string;
  skip?: boolean;
  pollInterval?: number;
  fetchPolicy?: QueryHookOptions['fetchPolicy'];
  initialData?: InitialData<TItem>;
  fragment?: DocumentNode;
  fragmentName?: string;
} & TCustomVars;

type ListResponse<TItem, TPath extends string> = {
  [K in TPath]: TItem[];
} & {
  nextPageToken: string | null;
};

type NewStandardListHookResult<TItem, TPath extends string> = {
  [K in TPath]: TItem[] | undefined;
} & {
  hasNextPage: boolean;
  error?: Error;
  isLoading: boolean;
  isEmpty: boolean;
  nextPage: () => Promise<void>;
  isLoadingNextPage: boolean;
  refetch: () => void;
};

type QueryOptions = {
  typename: string;
  fragmentName: string;
  fetchPolicy?: QueryHookOptions['fetchPolicy'];
};

export function replaceFragment(
  document: DocumentNode,
  oldFragmentName: string,
  newFragment: DocumentNode,
  newFragmentName: string,
): DocumentNode {
  // First, filter out the old fragment definition from the original document
  const filteredDefinitions = document.definitions.filter(def => {
    if (def.kind === Kind.FRAGMENT_DEFINITION) {
      return def.name.value !== oldFragmentName;
    }
    return true;
  });

  // Update fragment spreads ONLY in the original document's definitions
  // This prevents replacing spreads inside the new fragment (which could cause cycles)
  const updatedOriginalDefinitions = filteredDefinitions.map(def => {
    const visited = visit(def, {
      FragmentSpread(node) {
        if (node.name.value === oldFragmentName) {
          return {
            ...node,
            name: {
              ...node.name,
              value: newFragmentName,
            },
          };
        }
        return undefined;
      },
    });
    return visited;
  });

  // Get new fragment definitions (excluding any that already exist in the document)
  const existingFragmentNames = new Set(
    updatedOriginalDefinitions
      .filter(def => def.kind === Kind.FRAGMENT_DEFINITION)
      .map(def => (def as { name: { value: string } }).name.value),
  );

  const newFragmentDefinitions = newFragment.definitions.filter(def => {
    if (def.kind === Kind.FRAGMENT_DEFINITION) {
      return !existingFragmentNames.has(def.name.value);
    }
    return false;
  });

  // Combine: updated original definitions + new fragment definitions (unmodified)
  return {
    ...document,
    definitions: [...updatedOriginalDefinitions, ...newFragmentDefinitions],
  };
}

export function withNewStandardListHook<
  TData extends Record<string, any>,
  TDefaultItem,
  TPath extends Extract<keyof TData, string>,
  TCustomVars extends Record<string, any> = {},
>(
  query: DocumentNode,
  dataPath: TPath,
  queryOptions: QueryOptions,
): <TItem = TDefaultItem>(
  options?: NewStandardListHookOptions<TCustomVars, TItem>,
) => NewStandardListHookResult<TItem, TPath> {
  return <TItem = TDefaultItem>(
    options: NewStandardListHookOptions<
      TCustomVars,
      TItem
    > = {} as NewStandardListHookOptions<TCustomVars, TItem>,
  ): NewStandardListHookResult<TItem, TPath> => {
    const {
      filter,
      pageSize,
      orderBy,
      skip,
      initialData,
      pollInterval,
      fetchPolicy,
      fragment,
      fragmentName,
      ...customVars
    } = options;

    const client = useApolloClient();
    const writtenQuery = useRef<boolean>(false);

    // Memoize the query document, replacing fragment if provided
    const activeQuery = useMemo(() => {
      if (fragment && fragmentName) {
        return replaceFragment(
          query,
          queryOptions.fragmentName,
          fragment,
          fragmentName,
        );
      }
      return query;
    }, [fragment, fragmentName]);

    useLayoutEffect(() => {
      if (initialData && !writtenQuery.current) {
        const variables = {
          filter: filter ?? null,
          pageSize: pageSize ?? null,
          orderBy: orderBy ?? null,
          ...(customVars as TCustomVars),
        };

        const existing = client.cache.readQuery({
          query: activeQuery,
          variables,
        });

        if (existing) {
          // We only write the query if it doesn't exist in the cache.
          return;
        }

        client.cache.writeQuery({
          query: activeQuery,
          variables,
          data: {
            [dataPath]: {
              __typename: queryOptions.typename,
              [dataPath]: initialData.items,
              nextPageToken: initialData.nextPageToken,
            },
          },
        });

        // We do not want to write the query again even if initial data
        // or options change.
        writtenQuery.current = true;
      }
    }, [
      initialData,
      filter,
      pageSize,
      orderBy,
      customVars,
      client,
      activeQuery,
    ]);

    const {
      data,
      error: networkError,
      loading,
      fetchMore,
      refetch,
      networkStatus,
    } = useQuery<TData, QueryVariables & TCustomVars>(activeQuery, {
      variables: {
        filter: filter ?? null,
        pageSize: pageSize ?? null,
        orderBy: orderBy ?? null,
        ...(customVars as TCustomVars),
      },
      pollInterval,
      skip,
      fetchPolicy:
        fetchPolicy ??
        queryOptions?.fetchPolicy ??
        (initialData ? 'cache-first' : undefined),
      notifyOnNetworkStatusChange: true,
    });

    // Get the nested data using the provided path
    const nestedData = data?.[dataPath];
    const typedData = getTypeOrNull(nestedData) as ListResponse<
      TItem,
      TPath
    > | null;
    const appError = getError(nestedData) ?? undefined;

    const loadedItems = typedData?.[dataPath] as TItem[] | undefined;
    const loadedToken = typedData?.nextPageToken;

    const [{ nextPageToken, items, isLoading, isLoadingNextPage }, setState] =
      useState<{
        isLoading: boolean;
        isLoadingNextPage: boolean;
        items?: TItem[];
        nextPageToken: string | null | undefined;
      }>({
        isLoading: loading,
        items: loadedItems ?? undefined,
        nextPageToken: typedData?.nextPageToken ?? null,
        isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
      });

    const handleNextPage = useCallback(async () => {
      if (nextPageToken && fetchMore) {
        await fetchMore({
          variables: {
            pageToken: nextPageToken,
          },
        });
      }
    }, [nextPageToken, fetchMore]);

    useEffect(() => {
      if (loading) {
        setState(current => ({
          ...current,
          isLoading: true,
          isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
        }));
      } else if (loadedItems) {
        setState(current => ({
          ...current,
          items: loadedItems,
          isLoading: false,
          isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
          nextPageToken: loadedToken,
        }));
      } else {
        setState(current => ({
          ...current,
          isLoading: false,
          isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
        }));
      }
    }, [loading, loadedItems, loadedToken, networkStatus]);

    const isEmpty = items?.length === 0 && !appError && !networkError;

    return {
      [dataPath]: items,
      hasNextPage: !!nextPageToken,
      error: appError ?? networkError,
      isLoading,
      nextPage: handleNextPage,
      isLoadingNextPage,
      isEmpty,
      refetch,
    } as NewStandardListHookResult<TItem, TPath>;
  };
}
